/**
 * labels.ts — Nhãn tiếng Việt + định dạng dùng chung cho các tab chi tiết tổ chức (Platform).
 */

/** Hành động trong nhật ký Platform (platform_audit_logs.action). Hành động lạ → hiện nguyên chuỗi. */
export const PLATFORM_ACTION_LABELS: Record<string, string> = {
  'org.create': 'Tạo tổ chức',
  'org.update': 'Sửa tổ chức',
  'org.license': 'Đổi hạn cấp phép',
  'org.plan_change': 'Đổi gói',
  'org.extend': 'Gia hạn thủ công',
  'org.contract': 'Sửa hợp đồng Doanh nghiệp',
  'org.suspend': 'Khoá tổ chức',
  'org.unsuspend': 'Mở khoá tổ chức',
  'org.settings': 'Sửa cài đặt tổ chức',
  'org.reseed_roles': 'Khởi tạo lại vai trò',
  'org.company_update': 'Sửa thông tin công ty',
  'org.delete': 'Xoá tổ chức',
  'org.restore': 'Khôi phục tổ chức',
  'org.purge': 'Xoá vĩnh viễn tổ chức',
  'org.purge_failed': 'Xoá vĩnh viễn lỗi',
  'account.create': 'Tạo tài khoản',
  'account.update': 'Sửa tài khoản',
  'account.reset_password': 'Đặt lại mật khẩu',
  'account.lock': 'Khoá nhân viên',
  'account.unlock': 'Mở khoá nhân viên',
  'account.transfer_owner': 'Chuyển quyền chủ sở hữu',
  'account.revoke_sessions': 'Đăng xuất mọi phiên',
  'account.unlink_google': 'Gỡ liên kết Google',
  'channel.disable': 'Tắt kênh',
  'channel.enable': 'Bật kênh',
  enter_company: 'Vào công ty (impersonation)',
  'admin.self_update': 'Đổi thông tin cá nhân',
  'admin.password_change': 'Đổi mật khẩu super-admin',
  'billing.pricing': 'Sửa bảng giá',
}
export const platformActionLabel = (a: string) => PLATFORM_ACTION_LABELS[a] ?? a

export const ORDER_STATUS: Record<string, { text: string; variant: 'success' | 'warning' | 'destructive' | 'secondary' }> = {
  paid: { text: 'Đã thanh toán', variant: 'success' },
  pending: { text: 'Chờ thanh toán', variant: 'warning' },
  failed: { text: 'Thất bại', variant: 'destructive' },
  expired: { text: 'Hết hạn', variant: 'secondary' },
  cancelled: { text: 'Đã huỷ', variant: 'secondary' },
  underpaid: { text: 'Thiếu tiền', variant: 'destructive' },
}

export const INVOICE_STATUS: Record<string, string> = {
  none: '—',
  pending: 'Chờ xuất',
  issued: 'Đã xuất',
  failed: 'Lỗi xuất',
  cancelled: 'Đã huỷ',
}

export function providerLabel(p: string): string {
  switch (p) {
    case 'evopay_card': return 'Thẻ / ví điện tử (2C2P)'
    case 'evopay_bank': return 'Chuyển khoản QR (VietQR)'
    case 'evopay_paypal': return 'PayPal (USD)'
    case 'evopay_sepay': return 'Chuyển khoản (cổng SePay)'
    case 'evopay_tingee': return 'VietQR / thẻ ATM (cổng Tingee)'
    case 'promo': return 'Mã ưu đãi (0đ)'
    // Đơn cũ của cổng đã gỡ (07/10/2026) — vẫn hiện trong lịch sử.
    case 'zalopay': return 'ZaloPay (cũ)'
    case 'sepay_pg': return 'SePay (cũ)'
    case 'sepay': return 'Chuyển khoản (cũ)'
    case 'credit': return 'Quy đổi gói cũ'
    case 'manual': return 'Cấp tay (Platform)'
    case 'bank_transfer': return 'Chuyển khoản (báo giá thủ công)'
    default: return p
  }
}

export const PRESENCE: Record<string, { text: string; dot: string }> = {
  online: { text: 'Trực tuyến', dot: 'bg-success' },
  busy: { text: 'Bận', dot: 'bg-warning' },
  offline: { text: 'Ngoại tuyến', dot: 'bg-muted-foreground/40' },
}

export const CHANNEL_STATE: Record<string, { text: string; variant: 'success' | 'destructive' | 'secondary' | 'outline' }> = {
  connected: { text: 'Đã kết nối', variant: 'success' },
  disconnected: { text: 'Mất kết nối', variant: 'destructive' },
  disabled: { text: 'Đã tắt', variant: 'secondary' },
  deleted: { text: 'Đã xoá', variant: 'outline' },
}

export function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('vi-VN', { dateStyle: 'short', timeStyle: 'short' })
}

export function fmtDay(v: string | null | undefined): string {
  if (!v) return '—'
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/** Giới hạn hạn mức: null = không giới hạn. */
export const limitText = (n: number | null | undefined) => (n == null ? '∞' : new Intl.NumberFormat('vi-VN').format(n))
